import {
  forwardRef,
  useImperativeHandle,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent,
  type ReactNode,
} from "react";
import { Box, InputBase, MenuItem, Select, Typography } from "@mui/material";
import type { ColDef, ICellEditorParams } from "ag-grid-community";
import { CustomButton } from "@/components/common/customButton";
import type { Deal } from "@/types/deal";
import {
  AMOUNT_FIELDS,
  groupedFromValue,
  parseGrouped,
} from "../pipelineAmounts";
import { GroupedNumberInput } from "./GroupedNumberInput";

type StackedCardEditorProps = ICellEditorParams<Deal> & {
  /** Fields shown in the cell, edited together in the card. */
  editFields: string[];
};

type Option = Record<string, unknown> & { id: unknown };

type FieldSpec = {
  field: string;
  label: string;
  editable: boolean;
} & (
  | { kind: "select"; multiple: boolean; options: Option[]; labelKey: string }
  | { kind: "amount" | "number" | "date" | "text" }
);

type Draft = Record<string, unknown>;

const idOf = (value: unknown): unknown =>
  value && typeof value === "object" && "id" in value
    ? (value as { id: unknown }).id
    : value;

/** Stored value -> what the card's input holds (ids for selects, strings otherwise). */
function toDraft(spec: FieldSpec, value: unknown): unknown {
  if (spec.kind === "select") {
    if (spec.multiple) {
      return Array.isArray(value)
        ? value.map(idOf).filter((v) => v != null)
        : [];
    }
    return idOf(value) ?? "";
  }
  if (spec.kind === "amount") return groupedFromValue(value);
  if (value === null || value === undefined) return "";
  return spec.kind === "date" ? String(value).slice(0, 10) : String(value);
}

/** Card input -> the value the field's column setter expects. */
function fromDraft(spec: FieldSpec, draft: unknown): unknown {
  if (spec.kind === "select") {
    const byId = (id: unknown) =>
      spec.options.find((o) => String(o.id) === String(id)) ?? null;
    return spec.multiple
      ? (draft as unknown[]).map(byId).filter(Boolean)
      : draft === "" || draft == null
        ? null
        : byId(draft);
  }
  if (spec.kind === "amount") return parseGrouped(String(draft));
  if (draft === "") return null;
  if (spec.kind === "number") {
    const n = Number(draft);
    return Number.isFinite(n) ? n : null;
  }
  return draft;
}

const sameDraft = (a: unknown, b: unknown) =>
  JSON.stringify(Array.isArray(a) ? [...a].map(String).sort() : a) ===
  JSON.stringify(Array.isArray(b) ? [...b].map(String).sort() : b);

function specFor(field: string, colDef: ColDef, editable: boolean): FieldSpec {
  const label = colDef.headerName ?? field;
  // Dropdown columns (createSingleSelectColumn & co.) carry their options in
  // cellEditorParams.values.
  const params = (colDef.cellEditorParams ?? {}) as {
    values?: Option[];
    originalValues?: Option[];
    labelKey?: string;
    multiple?: boolean;
  };
  if (Array.isArray(params.values)) {
    return {
      field,
      label,
      editable,
      kind: "select",
      multiple: Boolean(params.multiple),
      options: params.originalValues ?? params.values ?? [],
      labelKey: params.labelKey ?? "label",
    };
  }
  const kind = AMOUNT_FIELDS.has(field)
    ? "amount"
    : colDef.cellDataType === "number"
      ? "number"
      : colDef.cellDataType === "dateString"
        ? "date"
        : "text";
  return { field, label, editable, kind };
}

const inputSx = {
  height: 32,
  px: 1.25,
  width: "100%",
  border: "1px solid #C4C4C4",
  borderRadius: "8px",
  fontSize: 12,
  fontFamily: '"Helvetica Neue", sans-serif',
  color: "#182737",
  backgroundColor: "#fff",
  "&.Mui-focused": { borderColor: "#245EA0" },
  "&.Mui-disabled": { backgroundColor: "#f7f8fa" },
} as const;

/**
 * Popup card for a two-line Meeting cell: one input per value in the cell,
 * using each field's own dropdown options. Save writes only the changed
 * fields through their real columns, so each goes through the normal
 * per-cell save (live updates, conflict checks).
 */
export const StackedCardEditor = forwardRef<unknown, StackedCardEditorProps>(
  (props, ref) => {
    const { api, node, column, editFields } = props;
    const specs = useMemo(
      () =>
        editFields.flatMap((field) => {
          const col = api.getColumn(field);
          if (!col) return [];
          return [specFor(field, col.getColDef(), col.isCellEditable(node))];
        }),
      [api, editFields, node],
    );
    const initial = useMemo<Draft>(() => {
      const data = (node.data ?? {}) as unknown as Record<string, unknown>;
      return Object.fromEntries(
        specs.map((spec) => [spec.field, toDraft(spec, data[spec.field])]),
      );
    }, [node, specs]);
    const [draft, setDraft] = useState<Draft>(initial);

    // AG Grid places the popup under the cell before it has a size, so near
    // the grid's right or bottom edge the card would be cut off: nudge it in.
    const cardRef = useRef<HTMLDivElement>(null);
    const [offset, setOffset] = useState({ x: 0, y: 0 });
    useLayoutEffect(() => {
      const card = cardRef.current;
      const bounds = card?.closest(".ag-root-wrapper")?.getBoundingClientRect();
      if (!card || !bounds) return;
      const rect = card.getBoundingClientRect();
      setOffset({
        x: Math.min(0, bounds.right - 8 - rect.right),
        y: Math.min(0, bounds.bottom - 8 - rect.bottom),
      });
    }, []);

    useImperativeHandle(ref, () => ({
      // Values are written through each field's own column, never this cell.
      isCancelAfterEnd: () => true,
    }));

    const save = () => {
      for (const spec of specs) {
        if (!spec.editable) continue;
        if (sameDraft(draft[spec.field], initial[spec.field])) continue;
        node.setDataValue(spec.field, fromDraft(spec, draft[spec.field]));
      }
      api.stopEditing(true);
    };
    const cancel = () => api.stopEditing(true);

    const onEnter = (e: KeyboardEvent) => {
      if (e.key === "Enter") {
        e.preventDefault();
        save();
      }
    };

    const set = (field: string, value: unknown) =>
      setDraft((prev) => ({ ...prev, [field]: value }));

    return (
      <Box
        ref={cardRef}
        role="dialog"
        aria-label={`Edit ${column.getColDef().headerName ?? ""}`}
        sx={{
          width: 300,
          p: 2,
          display: "flex",
          flexDirection: "column",
          gap: 1.25,
          backgroundColor: "#fff",
          borderRadius: "12px",
          boxShadow: "0 8px 24px rgba(16, 24, 40, 0.18)",
          transform: `translate(${offset.x}px, ${offset.y}px)`,
          fontFamily: '"Helvetica Neue", sans-serif',
        }}
      >
        <Typography sx={{ fontSize: 13, fontWeight: 600, color: "#182737" }}>
          {column.getColDef().headerName}
        </Typography>
        {specs.map((spec) => (
          <Field key={spec.field} label={spec.label}>
            {spec.kind === "select" ? (
              <Select
                size="small"
                fullWidth
                multiple={spec.multiple}
                displayEmpty
                disabled={!spec.editable}
                value={draft[spec.field] as string | string[]}
                onChange={(e) => set(spec.field, e.target.value)}
                className="custom-select"
                inputProps={{ "aria-label": spec.label }}
                renderValue={(selected) => {
                  const ids = Array.isArray(selected) ? selected : [selected];
                  const labels = ids
                    .map((id) =>
                      spec.options.find((o) => String(o.id) === String(id)),
                    )
                    .filter(Boolean)
                    .map((o) => String(o![spec.labelKey] ?? o!.label ?? ""));
                  return labels.length > 0 ? labels.join(", ") : "–";
                }}
                MenuProps={{
                  // Portalled menu: tell AG Grid it belongs to this editor.
                  className: "ag-custom-component-popup",
                  MenuListProps: { className: "custom-select-menu-list" },
                  PaperProps: { style: { maxHeight: 300 } },
                }}
                sx={{ height: 32, fontSize: 12 }}
              >
                {!spec.multiple && (
                  <MenuItem value="">
                    <em>None</em>
                  </MenuItem>
                )}
                {spec.options.map((option) => (
                  <MenuItem key={String(option.id)} value={option.id as string}>
                    {String(option[spec.labelKey] ?? option.label ?? option.id)}
                  </MenuItem>
                ))}
              </Select>
            ) : spec.kind === "amount" ? (
              <GroupedNumberInput
                ariaLabel={spec.label}
                disabled={!spec.editable}
                value={draft[spec.field] as string}
                onChange={(value) => set(spec.field, value)}
                onKeyDown={onEnter}
                sx={inputSx}
              />
            ) : (
              <InputBase
                type={spec.kind === "date" ? "date" : "text"}
                inputMode={spec.kind === "number" ? "decimal" : undefined}
                disabled={!spec.editable}
                value={draft[spec.field] as string}
                onChange={(e) => set(spec.field, e.target.value)}
                onKeyDown={onEnter}
                inputProps={{ "aria-label": spec.label }}
                sx={inputSx}
              />
            )}
          </Field>
        ))}
        <Box
          sx={{ display: "flex", justifyContent: "flex-end", gap: 1, mt: 0.5 }}
        >
          <CustomButton
            handleButtonPress={cancel}
            isButtonDisabled={false}
            text="Cancel"
            solid={false}
            heightOverride="30px"
          />
          <CustomButton
            handleButtonPress={save}
            isButtonDisabled={false}
            text="Save"
            heightOverride="30px"
          />
        </Box>
      </Box>
    );
  },
);

function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <Box>
      <Typography sx={{ mb: 0.5, fontSize: 12, color: "#344054" }}>
        {label}
      </Typography>
      {children}
    </Box>
  );
}
